import { supabase } from './supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface RealtimeSubscription {
  unsubscribe: () => void;
}

export class TaskRealtimeService {
  private channels: Map<string, RealtimeChannel> = new Map();

  // Subscribe to new tasks published in a specific country
  subscribeToTasksByCountry(
    countryCode: string,
    callback: (payload: any) => void,
    excludeUserId?: string
  ): RealtimeSubscription {
    const channelName = `tasks-${countryCode}-${Date.now()}`; // Add timestamp to avoid conflicts
    
    // Clean up any existing channel with similar name
    this.channels.forEach((channel, name) => {
      if (name.startsWith(`tasks-${countryCode}`)) {
        channel.unsubscribe();
        this.channels.delete(name);
      }
    });
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tasks'
          // Remove the filter for now to test if basic realtime works
          // filter: `country_code=eq.${countryCode} AND status=eq.open`
        },
        (payload) => {
          // Apply filtering in JavaScript instead of SQL
          const task = payload.new;
          if (!task?.id || !task?.title) {
            return;
          }
          
          // Filter by country code
          if (task.country_code !== countryCode) {
            return;
          }
          
          // Filter by status
          if (task.status !== 'open') {
            return;
          }
          
          // Exclude tasks created by the current user
          if (excludeUserId && task.created_by === excludeUserId) {
            return;
          }
          
          // Send the task data with the new property structure
          callback({ 
            eventType: 'NEW_PUBLISHED',
            new: task  // Keep the nested structure that the hook expects
          });
        }
      )
      .subscribe();

    this.channels.set(channelName, channel);

    return {
      unsubscribe: () => {
        channel.unsubscribe();
        this.channels.delete(channelName);
      }
    };
  }

  // Subscribe to task submissions (for task creators to see new submissions)
  subscribeToTaskSubmissions(
    taskId: string,
    callback: (payload: any) => void
  ): RealtimeSubscription {
    const channelName = `task-submissions-${taskId}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_submissions',
          filter: `task_id=eq.${taskId}`
        },
        (payload) => {
          callback({ ...payload, eventType: 'INSERT' });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'task_submissions',
          filter: `task_id=eq.${taskId}`
        },
        (payload) => {
          callback({ ...payload, eventType: 'UPDATE' });
        }
      )
      .subscribe();

    this.channels.set(channelName, channel);

    return {
      unsubscribe: () => {
        channel.unsubscribe();
        this.channels.delete(channelName);
      }
    };
  }

  // Subscribe to user's task submissions (for task takers to see status updates)
  subscribeToUserSubmissions(
    userId: string,
    callback: (payload: any) => void
  ): RealtimeSubscription {
    const channelName = `user-submissions-${userId}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'task_submissions',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('User submission status updated:', payload);
          callback({ ...payload, eventType: 'STATUS_UPDATE' });
        }
      )
      .subscribe();

    this.channels.set(channelName, channel);

    return {
      unsubscribe: () => {
        channel.unsubscribe();
        this.channels.delete(channelName);
      }
    };
  }

  // Cleanup all subscriptions
  unsubscribeAll(): void {
    this.channels.forEach(channel => channel.unsubscribe());
    this.channels.clear();
  }
}

// Create a singleton instance
export const taskRealtimeService = new TaskRealtimeService();
